<template>
    <v-dialog
        :value="value"
        @input="$emit('input', $event)"
        max-width="800"
        scrollable
        persistent
        :fullscreen="phoneOnly"
        :transition="phoneOnly ? 'dialog-bottom-transition' : 'dialog-transition'"
    >
        <v-card
            v-if="enrollment"
            :loading="loadingConfig"
            :class="{'fill-height d-flex flex-column': phoneOnly}"
            :style="iosSafeArea ? 'padding-top: 45px!important;' : ''"
        >
            <v-card-title class="d-flex align-start" :class="{'px-4 py-3': phoneOnly, 'py-4': pcOnly}">
                <v-icon class="me-2 mt-1" color="primary" :small="phoneOnly">mdi-file-document-edit-outline</v-icon>
                <div class="flex-grow-1" style="min-width: 0;">
                    <div :class="phoneOnly ? 'subtitle-1 font-weight-medium' : ''" style="line-height: 1.2;">
                        {{ isResubmit ? 'Edit & Resubmit' : 'Quarterly Report' }}
                    </div>
                    <div class="caption muted--text mt-1" style="white-space: normal; word-break: break-word; line-height: 1.3;">
                        {{ enrollment.user && enrollment.user.name }} — {{ enrollment.course && enrollment.course.name }}
                    </div>
                    <v-alert v-if="isResubmit" type="warning" dense outlined class="mt-2 mb-0 caption" icon="mdi-undo-variant">
                        This report was sent back. Edit at least one field before re-submitting.
                    </v-alert>
                </div>
                <v-btn icon @click="close" :disabled="loading" class="ms-2">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider />

            <v-card-text v-if="config" class="pt-4 qr-form" :class="{'px-4': phoneOnly, 'flex-grow-1': phoneOnly}">
                <!-- Student Level (direct question) -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">What's your student's level? *</label>
                    </div>
                    <v-select
                        :value="form.level"
                        @change="setLevel"
                        :items="levelOptions"
                        outlined dense
                        hide-details="auto"
                        placeholder="Select a level"
                        prepend-inner-icon="mdi-stairs-up"
                        :error="submitted && !form.level"
                        :error-messages="submitted && !form.level ? ['Please select a level.'] : []"
                    />
                </div>

                <v-divider class="my-4" />

                <!-- Progress Summary -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">Progress Summary *</label>
                        <v-btn v-if="config.templates && config.templates.progress_summary" x-small text color="cyan darken-1" class="template-btn" @click="useTemplate('progress_summary')">
                            <v-icon x-small left>mdi-content-paste</v-icon>Insert template
                        </v-btn>
                    </div>
                    <v-textarea
                        v-model="form.progress_summary"
                        outlined dense :rows="phoneOnly ? 4 : 5" hide-details
                        placeholder="Describe the student's progress this quarter..."
                    />
                    <div class="word-counter" :class="{ 'word-counter--ok': counts.progress_summary >= minWords.progress_summary }">
                        <v-icon x-small>{{ counts.progress_summary >= minWords.progress_summary ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                        {{ counts.progress_summary }} / {{ minWords.progress_summary }} words
                    </div>
                </div>

                <v-divider class="my-4" />

                <!-- Topics Covered -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">Topics Covered *</label>
                        <v-btn v-if="config.templates && config.templates.topics_covered" x-small text color="cyan darken-1" class="template-btn" @click="useTemplate('topics_covered')">
                            <v-icon x-small left>mdi-content-paste</v-icon>Insert template
                        </v-btn>
                    </div>
                    <div v-for="(topic, i) in form.topics_covered" :key="i" class="d-flex align-center mb-2">
                        <v-text-field v-model="form.topics_covered[i]" outlined dense hide-details :placeholder="`Topic ${i + 1}`" class="me-2" style="min-width: 0;" />
                        <v-btn icon small color="error" @click="removeTopic(i)" :disabled="form.topics_covered.length <= 1" class="flex-shrink-0">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </div>
                    <v-btn x-small text color="primary" class="mt-1" @click="addTopic">
                        <v-icon x-small left>mdi-plus</v-icon>Add topic
                    </v-btn>
                    <div class="word-counter" :class="{ 'word-counter--ok': filledTopicsCount >= minTopics }">
                        <v-icon x-small>{{ filledTopicsCount >= minTopics ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                        {{ filledTopicsCount }} / {{ minTopics }} topics
                    </div>
                </div>

                <v-divider class="my-4" />

                <!-- Performance Areas -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">Performance Areas *</label>
                        <span class="caption muted--text template-btn">Scale: 1 (poor) – 5 (excellent)</span>
                    </div>
                    <div
                        v-for="area in config.performance_areas"
                        :key="area"
                        class="mb-3"
                        :class="phoneOnly ? '' : 'd-flex align-center justify-space-between'"
                    >
                        <div class="text--text" :class="{'mb-1': phoneOnly}" style="word-break: break-word;">{{ area }}</div>
                        <div class="d-flex align-center">
                            <span class="me-2 caption muted--text" style="min-width: 12px;">{{ form.performance_areas[area] || 0 }}</span>
                            <v-rating
                                :value="form.performance_areas[area] || 0"
                                @input="setRating(area, $event)"
                                color="primary"
                                background-color="muted lighten-2"
                                hover length="5" dense
                                :size="phoneOnly ? 28 : 22"
                            />
                        </div>
                    </div>
                    <v-card flat outlined class="mt-3 pa-3">
                        <div class="d-flex align-center justify-space-between flex-wrap" style="row-gap: 8px;">
                            <div>
                                <div class="caption muted--text">Average score</div>
                                <div class="font-weight-bold" style="font-size: 1.3rem">{{ averageScore.toFixed(2) }} / 5</div>
                            </div>
                            <v-chip :color="statusColor" dark label>{{ statusLabel }}</v-chip>
                        </div>
                    </v-card>
                </div>

                <v-divider class="my-4" />

                <!-- Areas for Improvement -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">Areas for Improvement *</label>
                        <v-btn v-if="config.templates && config.templates.areas_for_improvement" x-small text color="cyan darken-1" class="template-btn" @click="useTemplate('areas_for_improvement')">
                            <v-icon x-small left>mdi-content-paste</v-icon>Insert template
                        </v-btn>
                    </div>
                    <v-textarea
                        v-model="form.areas_for_improvement"
                        outlined dense :rows="phoneOnly ? 4 : 4" hide-details
                    />
                    <div class="word-counter" :class="{ 'word-counter--ok': counts.areas_for_improvement >= minWords.areas_for_improvement }">
                        <v-icon x-small>{{ counts.areas_for_improvement >= minWords.areas_for_improvement ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                        {{ counts.areas_for_improvement }} / {{ minWords.areas_for_improvement }} words
                    </div>
                </div>

                <v-divider class="my-4" />

                <!-- Next Phase Plan -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">Next Phase Plan *</label>
                        <v-btn v-if="config.templates && config.templates.next_phase_plan" x-small text color="cyan darken-1" class="template-btn" @click="useTemplate('next_phase_plan')">
                            <v-icon x-small left>mdi-content-paste</v-icon>Insert template
                        </v-btn>
                    </div>
                    <v-textarea
                        v-model="form.next_phase_plan"
                        outlined dense :rows="phoneOnly ? 4 : 3" hide-details
                    />
                    <div class="word-counter" :class="{ 'word-counter--ok': counts.next_phase_plan >= minWords.next_phase_plan }">
                        <v-icon x-small>{{ counts.next_phase_plan >= minWords.next_phase_plan ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                        {{ counts.next_phase_plan }} / {{ minWords.next_phase_plan }} words
                    </div>
                </div>

                <v-divider class="my-4" />

                <!-- Teacher's Message -->
                <div class="field-group">
                    <div class="field-label-row">
                        <label class="field-label">Teacher's Message *</label>
                        <v-btn v-if="config.templates && config.templates.teachers_message" x-small text color="cyan darken-1" class="template-btn" @click="useTemplate('teachers_message')">
                            <v-icon x-small left>mdi-content-paste</v-icon>Insert template
                        </v-btn>
                    </div>
                    <v-textarea
                        v-model="form.teachers_message"
                        outlined dense :rows="phoneOnly ? 4 : 3" hide-details
                    />
                    <div class="word-counter" :class="{ 'word-counter--ok': counts.teachers_message >= minWords.teachers_message }">
                        <v-icon x-small>{{ counts.teachers_message >= minWords.teachers_message ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                        {{ counts.teachers_message }} / {{ minWords.teachers_message }} words
                    </div>
                </div>
            </v-card-text>
            <v-card-text v-else-if="loadingConfig" class="text-center py-6">
                <v-progress-circular indeterminate color="primary" />
            </v-card-text>

            <v-divider />
            <v-card-actions class="px-4 py-3" :class="{'pb-safe': iosSafeArea}">
                <template v-if="phoneOnly">
                    <v-btn text @click="close" :disabled="loading">Cancel</v-btn>
                    <v-spacer />
                    <v-btn color="primary" depressed :loading="loading" :disabled="!canSubmit" @click="submit">
                        {{ isResubmit ? 'Resubmit' : 'Submit Report' }}
                    </v-btn>
                </template>
                <template v-else>
                    <v-spacer />
                    <v-btn text @click="close" :disabled="loading">Cancel</v-btn>
                    <v-btn color="primary" :loading="loading" :disabled="!canSubmit" @click="submit">{{ isResubmit ? 'Resubmit' : 'Submit Report' }}</v-btn>
                </template>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed';
import GlobalMethods from '@/helpers/global-methods';

const DEFAULT_MIN_WORDS = {
    progress_summary: 60,
    areas_for_improvement: 30,
    next_phase_plan: 30,
    teachers_message: 25,
};

function countWords(text) {
    if (!text) return 0;
    const trimmed = String(text).trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).filter(Boolean).length;
}

function blankForm() {
    return {
        level: null,
        progress_summary: '',
        topics_covered: ['', '', ''],
        performance_areas: {},
        areas_for_improvement: '',
        next_phase_plan: '',
        teachers_message: '',
    };
}

export default {
    props: {
        value: { type: Boolean, default: false },
        enrollment: { type: Object, default: null }, // {enrollment_id, user, course, category}
    },
    data() {
        return {
            form: blankForm(),
            config: null,
            loadingConfig: false,
            loading: false,
            submitted: false,
            levelOptions: [
                { text: 'Beginner', value: 'beginner' },
                { text: 'Intermediate', value: 'intermediate' },
                { text: 'Advanced', value: 'advanced' },
            ],
        };
    },
    computed: {
        ...GlobalComputed,
        isResubmit() {
            return !!(this.enrollment && this.enrollment.reversed_report);
        },
        minWords() {
            return (this.config && this.config.min_words) || DEFAULT_MIN_WORDS;
        },
        minTopics() {
            return (this.config && this.config.min_topics) || 3;
        },
        thresholds() {
            return (this.config && this.config.status_thresholds) || { on_track: 4.0, needs_support: 2.5 };
        },
        counts() {
            return {
                progress_summary:      countWords(this.form.progress_summary),
                areas_for_improvement: countWords(this.form.areas_for_improvement),
                next_phase_plan:       countWords(this.form.next_phase_plan),
                teachers_message:      countWords(this.form.teachers_message),
            };
        },
        filledTopicsCount() {
            return (this.form.topics_covered || []).filter(t => t && t.trim()).length;
        },
        averageScore() {
            const values = Object.values(this.form.performance_areas || {})
                .map(v => Number(v))
                .filter(v => !Number.isNaN(v) && v > 0);
            if (!values.length) return 0;
            return values.reduce((a, b) => a + b, 0) / values.length;
        },
        studentStatus() {
            const avg = this.averageScore;
            if (avg >= this.thresholds.on_track) return 'on_track';
            if (avg >= this.thresholds.needs_support) return 'needs_support';
            return 'at_risk';
        },
        statusLabel() {
            return ({ on_track: 'On Track', needs_support: 'Needs Support', at_risk: 'At Risk' })[this.studentStatus];
        },
        statusColor() {
            return ({ on_track: 'success', needs_support: 'warning', at_risk: 'error' })[this.studentStatus];
        },
        canSubmit() {
            if (!this.config) return false;
            const allRated = (this.config.performance_areas || []).every(area => Number(this.form.performance_areas[area] || 0) > 0);
            return (
                !!this.form.level &&
                this.counts.progress_summary >= this.minWords.progress_summary &&
                this.counts.areas_for_improvement >= this.minWords.areas_for_improvement &&
                this.counts.next_phase_plan >= this.minWords.next_phase_plan &&
                this.counts.teachers_message >= this.minWords.teachers_message &&
                this.filledTopicsCount >= this.minTopics &&
                allRated
            );
        },
    },
    watch: {
        value(open) {
            if (open && this.enrollment) {
                this.reset();
                this.fetchConfig();
            }
        },
    },
    methods: {
        ...GlobalMethods,
        reset() {
            this.form = blankForm();
            this.config = null;
            this.submitted = false;
            this.loading = false;
        },
        close() {
            if (this.loading) return;
            this.$emit('input', false);
        },
        fetchConfig() {
            if (!this.enrollment) return;
            this.loadingConfig = true;
            this.$store.dispatch('loadQuarterlyFormConfig', this.enrollment.enrollment_id)
                .then(response => {
                    this.config = response.data;
                    const areas = (this.config.performance_areas || []);
                    const reversed = this.enrollment && this.enrollment.reversed_report;
                    if (reversed) {
                        // Pre-fill with the report the admin sent back so the teacher edits in place.
                        // Keep existing rating keys if they match the current config; fall back to 0 for any new area.
                        const performance = {};
                        areas.forEach(a => { performance[a] = Number((reversed.performance_areas || {})[a]) || 0; });
                        this.$set(this.form, 'performance_areas', performance);
                        this.$set(this.form, 'level',                 reversed.level || null);
                        this.$set(this.form, 'progress_summary',      reversed.progress_summary || '');
                        this.$set(this.form, 'topics_covered',        (reversed.topics_covered && reversed.topics_covered.length) ? [...reversed.topics_covered] : ['', '', '']);
                        this.$set(this.form, 'areas_for_improvement', reversed.areas_for_improvement || '');
                        this.$set(this.form, 'next_phase_plan',       reversed.next_phase_plan || '');
                        this.$set(this.form, 'teachers_message',      reversed.teachers_message || '');
                    } else {
                        const performance = {};
                        areas.forEach(a => { performance[a] = 0; });
                        this.$set(this.form, 'performance_areas', performance);
                    }
                })
                .catch(() => { this.showMsg('Failed to load report form.', 'error'); })
                .finally(() => { this.loadingConfig = false; });
        },
        addTopic() { this.form.topics_covered.push(''); },
        removeTopic(i) { this.form.topics_covered.splice(i, 1); },
        setRating(area, val) { this.$set(this.form.performance_areas, area, val); },
        setLevel(val) { this.$set(this.form, 'level', val); },
        useTemplate(field) {
            const tpl = this.config && this.config.templates && this.config.templates[field];
            if (!tpl) return;
            if (Array.isArray(tpl)) {
                this.$set(this.form, field, [...tpl]);
            } else {
                this.$set(this.form, field, tpl);
            }
        },
        submit() {
            this.submitted = true;
            if (!this.canSubmit || !this.enrollment) return;
            this.loading = true;
            const payload = {
                level: this.form.level,
                progress_summary: this.form.progress_summary,
                topics_covered: (this.form.topics_covered || []).filter(t => t && t.trim()),
                performance_areas: this.form.performance_areas,
                areas_for_improvement: this.form.areas_for_improvement,
                next_phase_plan: this.form.next_phase_plan,
                teachers_message: this.form.teachers_message,
            };
            const action = this.isResubmit ? 'resubmitQuarterlyReport' : 'submitQuarterlyReport';
            const args = this.isResubmit
                ? { reportId: this.enrollment.reversed_report.id, data: payload }
                : { enrollmentId: this.enrollment.enrollment_id, data: payload };
            this.$store.dispatch(action, args)
                .then(() => {
                    this.$emit('submitted');
                    this.$emit('input', false);
                })
                .catch(error => {
                    const msg = (error && error.response && error.response.data && error.response.data.message) || 'Failed to submit report.';
                    this.showMsg(msg, 'error');
                })
                .finally(() => { this.loading = false; });
        },
    },
};
</script>

<style scoped>
.pb-safe {
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 12px) !important;
}
.qr-form .field-group {
    margin-bottom: 4px;
}
.qr-form .field-label-row {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
}
.qr-form .field-label {
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.qr-form .template-btn {
    margin-left: auto !important;
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 500 !important;
    height: 22px !important;
}
.qr-form .word-counter {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #c44b4b;
    margin-top: 6px;
    padding: 3px 8px;
    background: #fff0f0;
    border-radius: 10px;
    font-weight: 500;
}
.qr-form .word-counter--ok {
    color: #2d8659;
    background: #e8f5ed;
}
.qr-form .word-counter .v-icon {
    color: inherit !important;
}
</style>
